import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flame, Mail, FileText, Mic, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ApplyForm() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Name and email are required"); return; }
    setLoading(true);
    try {
      const r = await api.post("/form-submit", { name, email, phone });
      setDone(r.data);
      if (r.data.email_sent) toast.success("Application received — check your email!");
      else toast.info("Application received. Continue below.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const continueLink = done?.continue_link;
  const cid = done?.candidate?.id;

  return (
    <div className="min-h-screen bg-white flex" data-testid="apply-form-page">
      {/* left brand panel */}
      <div className="hidden lg:flex w-[42%] bg-[#E03C31] flex-col justify-between p-12 text-white relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur">
            <Flame className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <span className="font-display text-xl font-semibold">Ignite CX</span>
        </div>
        <div>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold leading-tight tracking-tight">
            Apply in minutes.<br />Get hired faster.
          </h1>
          <p className="mt-5 text-white/80 text-base max-w-md leading-relaxed">
            Fill out the form and we'll email you the next steps: upload your resume and record a short voice intro. That's it.
          </p>
          <div className="mt-10 space-y-4">
            <Step icon={Mail} text="We email you the requirements instantly" />
            <Step icon={FileText} text="Upload your resume" />
            <Step icon={Mic} text="Record a short voice reading of our script" />
          </div>
        </div>
        <div className="text-white/60 text-sm">Customer Experience Associate · Remote</div>
        <div className="absolute -bottom-24 -right-24 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-10 -right-10 w-40 h-40 rounded-full bg-white/5" />
      </div>

      {/* right form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {!done ? (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              <div className="lg:hidden flex items-center gap-2 mb-8">
                <div className="w-9 h-9 rounded-lg bg-[#E03C31] flex items-center justify-center">
                  <Flame className="w-5 h-5 text-white" strokeWidth={1.5} />
                </div>
                <span className="font-display text-lg font-semibold">Ignite CX</span>
              </div>
              <h2 className="font-display text-3xl font-semibold tracking-tight text-zinc-900">Start your application</h2>
              <p className="text-sm text-zinc-500 mt-2 mb-8">Tell us who you are. We'll email you what to do next.</p>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Full name</Label>
                  <Input data-testid="form-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Email address</Label>
                  <Input data-testid="form-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@example.com" className="mt-1.5 h-11" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Phone <span className="normal-case text-zinc-400">(optional)</span></Label>
                  <Input data-testid="form-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000 0000" className="mt-1.5 h-11" />
                </div>
                <Button data-testid="form-submit-btn" onClick={submit} disabled={loading} className="w-full h-11 bg-[#E03C31] hover:bg-[#C9362C] gap-2 active:scale-[0.98] transition-transform">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  Submit application
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} className="text-center" data-testid="form-success">
              <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-9 h-9 text-emerald-600" strokeWidth={1.5} />
              </div>
              <h2 className="font-display text-2xl font-semibold text-zinc-900">You're in, {done.candidate?.name?.split(" ")[0]}!</h2>
              <p className="text-sm text-zinc-500 mt-3 leading-relaxed">
                {done.email_sent
                  ? <>We've emailed <strong className="text-zinc-700">{done.candidate?.email}</strong> with the next steps. You can also continue right here to upload your resume and record your voice.</>
                  : <>Your application is saved. Continue below to upload your resume and record your voice.</>}
              </p>
              <Button data-testid="form-continue-btn" onClick={() => navigate(`/candidate?cid=${cid}`)} className="mt-7 w-full h-11 bg-[#E03C31] hover:bg-[#C9362C] gap-2">
                Continue now <ArrowRight className="w-4 h-4" />
              </Button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

const Step = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
      <Icon className="w-4.5 h-4.5" strokeWidth={1.5} />
    </div>
    <span className="text-white/90 text-sm">{text}</span>
  </div>
);
